import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, getDocs, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useApp } from '../App';
import { EventTypes, ScheduleStatuses } from '../utils/constants';

// 실제 스키마의 정식 유형만 노출 (레거시 키로 신규 저장되는 것 방지)
const CANONICAL_EVENT_TYPES = ['training', 'regular_game', 'practice_game', 'training_with_game', 'meeting', 'other'];
const GAME_TYPES = ['regular_game', 'practice_game', 'training_with_game'];

export default function ScheduleForm({ onDone, copyFrom }) {
  const { currentTeam } = useApp();

  // 아이스링크 목록 (장소 콤보박스용)
  const [rinks, setRinks] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const locationRef = useRef(null);

  // copyFrom이 있으면 복사 데이터로 초기화 (+7일)
  const getInitialForm = () => {
    if (copyFrom) {
      const addWeek = (ts) => {
        if (!ts) return '';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        d.setDate(d.getDate() + 7); // +1주일
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };
      return {
        title: copyFrom.title || '',
        status: 'draft',
        eventType: copyFrom.eventType || 'training',
        date: addWeek(copyFrom.date),
        endDate: addWeek(copyFrom.endDate),
        location: copyFrom.location || '',
        fee: copyFrom.fee ? String(copyFrom.fee) : '',
        description: copyFrom.description || '',
        opponentTeamName: copyFrom.opponentTeamName || '',
      };
    }
    return {
      title: '', status: 'open', eventType: 'training', date: '', endDate: '',
      location: '', fee: '', description: '', opponentTeamName: '',
    };
  };

  const [form, setForm] = useState(getInitialForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadRinks(); }, []);

  // 클릭 외부 감지
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target)) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRinks = async () => {
    try {
      const snap = await getDocs(collection(db, 'iceRinks'));
      setRinks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  };

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleLocationSelect = (rinkName) => {
    handleChange('location', rinkName);
    setLocationSearch('');
    setShowLocationDropdown(false);
  };

  const filteredRinks = rinks.filter(r =>
    !locationSearch || r.name?.toLowerCase().includes(locationSearch.toLowerCase()) ||
    r.address?.toLowerCase().includes(locationSearch.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date) return alert('제목과 날짜를 입력하세요.');
    setSaving(true);
    try {
      await addDoc(collection(db, 'teams', currentTeam.id, 'schedules'), {
        teamId: currentTeam.id,
        title: form.title,
        status: form.status,
        eventType: form.eventType,
        date: Timestamp.fromDate(new Date(form.date)),
        endDate: form.endDate ? Timestamp.fromDate(new Date(form.endDate)) : null,
        location: form.location,
        fee: form.fee ? parseInt(form.fee) : 0,
        description: form.description,
        opponentTeamName: form.opponentTeamName || null,
        createdBy: auth.currentUser.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      alert('일정이 등록되었습니다!');
      onDone();
    } catch (err) { alert('등록 실패: ' + err.message); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={s.panel}>
        <div style={s.panelHeader}>
          <h2 style={s.panelTitle}>
            {copyFrom ? '📋 일정 복사' : '➕ 일정 등록'}
          </h2>
          {copyFrom && (
            <span style={{ fontSize: 11, color: '#778899' }}>
              "{copyFrom.title}" 에서 복사됨
            </span>
          )}
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Field label="제목 *">
            <input style={s.input} value={form.title} onChange={e => handleChange('title', e.target.value)} placeholder="예: 정기 훈련" required />
          </Field>
          <Field label="공개 상태">
            <div style={{ display: 'flex', gap: 6 }}>
              {Object.values(ScheduleStatuses).filter(o => o.value !== 'cancelled').map(o => {
                const on = form.status === o.value;
                return (
                  <button key={o.value} type="button" onClick={() => handleChange('status', o.value)} title={o.desc}
                    style={{
                      flex: 1, padding: '10px 6px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      border: `1px solid ${on ? o.color : 'rgba(255,255,255,0.07)'}`,
                      backgroundColor: on ? `${o.color}1F` : 'transparent',
                      color: on ? o.color : '#667788',
                    }}>{o.icon} {o.label}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: '#667788', marginTop: 6 }}>
              {ScheduleStatuses[form.status]?.desc}
            </div>
          </Field>

          <Field label="유형">
            <select style={s.input} value={form.eventType} onChange={e => handleChange('eventType', e.target.value)}>
              {CANONICAL_EVENT_TYPES.map(k => <option key={k} value={k}>{EventTypes[k]?.label || k}</option>)}
              {!CANONICAL_EVENT_TYPES.includes(form.eventType) && (
                <option value={form.eventType}>{EventTypes[form.eventType]?.label || form.eventType} (기존값)</option>
              )}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="시작 일시 *">
              <input type="datetime-local" style={s.input} value={form.date} onChange={e => handleChange('date', e.target.value)} required />
            </Field>
            <Field label="종료 일시">
              <input type="datetime-local" style={s.input} value={form.endDate} onChange={e => handleChange('endDate', e.target.value)} />
            </Field>
          </div>

          {/* 장소: 콤보박스 (아이스링크 선택 + 직접 입력) */}
          <Field label="장소">
            <div ref={locationRef} style={{ position: 'relative' }}>
              <input
                style={s.input}
                value={form.location}
                onChange={e => {
                  handleChange('location', e.target.value);
                  setLocationSearch(e.target.value);
                  setShowLocationDropdown(true);
                }}
                onFocus={() => setShowLocationDropdown(true)}
                placeholder="아이스링크 선택 또는 직접 입력"
              />
              {/* 드롭다운 화살표 */}
              <span
                onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#667788', fontSize: 12 }}
              >
                {showLocationDropdown ? '▲' : '▼'}
              </span>

              {/* 드롭다운 목록 */}
              {showLocationDropdown && filteredRinks.length > 0 && (
                <div style={s.dropdown}>
                  {filteredRinks.slice(0, 10).map(rink => (
                    <div
                      key={rink.id}
                      onClick={() => handleLocationSelect(rink.name)}
                      style={s.dropdownItem}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(91,196,216,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div style={{ fontSize: 13, fontWeight: 500 }}>🏟️ {rink.name}</div>
                      {rink.address && <div style={{ fontSize: 11, color: '#667788', marginTop: 2 }}>{rink.address}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Field>

          <Field label="참가비 (원)">
            <input type="number" style={s.input} value={form.fee} onChange={e => handleChange('fee', e.target.value)} placeholder="0" />
          </Field>
          {GAME_TYPES.includes(form.eventType) && (
            <Field label="상대팀">
              <input style={s.input} value={form.opponentTeamName} onChange={e => handleChange('opponentTeamName', e.target.value)} placeholder="상대팀 이름" />
            </Field>
          )}
          <Field label="설명">
            <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={form.description} onChange={e => handleChange('description', e.target.value)} placeholder="추가 안내사항" />
          </Field>
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button type="submit" disabled={saving} style={s.submitBtn}>
              {saving ? '저장 중...' : copyFrom ? '일정 복사 등록' : '일정 등록'}
            </button>
            <button type="button" onClick={onDone} style={s.cancelBtn}>취소</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 12, color: '#8899AA', fontWeight: 600, display: 'block', marginBottom: 6, letterSpacing: 0.5 }}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  panelHeader: { padding: '14px 20px', borderBottom: '1px solid rgba(91,196,216,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  panelTitle: { margin: 0, fontSize: 15, fontWeight: 600 },
  input: { width: '100%', padding: '12px 14px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.1)', fontSize: 14, backgroundColor: 'rgba(10,18,32,0.8)', color: '#E8ECF1', boxSizing: 'border-box' },
  submitBtn: { padding: '12px 32px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #5BC4D8, #4AABB8)', color: '#0A1220', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  cancelBtn: { padding: '12px 24px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#8899AA', fontSize: 14, cursor: 'pointer' },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
    backgroundColor: '#141E30', border: '1px solid rgba(91,196,216,0.15)', borderRadius: 8,
    maxHeight: 240, overflowY: 'auto', zIndex: 50,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  },
  dropdownItem: {
    padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid rgba(91,196,216,0.04)',
    transition: 'background-color 0.1s',
  },
};